<template>
    <!-- 数据库控制台面板（嵌入右侧页签内） -->
    <div class="dbconsole-drawer dbconsole-panel">
        <div class="dbconsole-panel__head">
            <span class="dbconsole-head__name">{{ conn.name }}</span>
            <span class="dbconsole-head__addr">{{ conn.host }}:{{ conn.port }}</span>
            <el-tag size="small" :type="conn.canWrite ? 'warning' : 'info'" effect="plain">
                {{ conn.canWrite ? '可写' : '只读' }}</el-tag>
            <el-tag v-if="conn.prodLock" size="small" type="danger" effect="plain">生产防护</el-tag>
            <el-button class="dbconsole-panel__add" size="small" type="primary" plain @click="addBlock()">
                <el-icon class="el-icon--left">
                    <Plus />
                </el-icon>
                新建
            </el-button>
            <el-button class="dbconsole-panel__blocks-btn" size="small" @click="blocksDrawer = true">
                <el-icon class="el-icon--left">
                    <Collection />
                </el-icon>
                SQL 块
                <span v-if="savedBlockTotal" class="dbconsole-panel__blocks-count">{{ savedBlockTotal }}</span>
            </el-button>
        </div>

        <div class="dbconsole-body">
            <div v-if="blocks.length === 0" class="dbconsole-empty">
                <el-icon :size="22">
                    <EditPen />
                </el-icon>
                <p>点击「新建」开始编写 SQL</p>
            </div>
            <template v-else>
                <el-tabs v-model="activeBlockUid" class="dbconsole-tabs" closable
                    @tab-remove="removeBlockByUid" @tab-change="onBlockTabChange">
                    <el-tab-pane v-for="block in blocks" :key="block.uid" :name="block.uid">
                        <template #label>
                            <span class="dbconsole-query-tab" :title="blockTabName(block)">
                                <span class="dbconsole-query-tab__name">{{ blockTabName(block) }}</span>
                                <el-tag v-if="block.id && block.isShare" size="small" type="success" effect="plain">共享</el-tag>
                            </span>
                        </template>
                    </el-tab-pane>
                </el-tabs>

                <db-query-workbench v-if="activeBlock" ref="workbench" :key="activeBlock.uid"
                    :block="activeBlock" :conn="conn" :sql-mode="sqlMode"
                    v-model:sql-text="activeBlock.sqlText"
                    v-model:database="activeBlock.database"
                    v-model:active-result="activeBlock.activeResult"
                    :databases="databases" :db-loading="dbLoading"
                    :tables="tablesFor(activeBlock.database)"
                    :table-comments="tableCommentsFor(activeBlock.database)"
                    :columns="columnsFor(activeBlock.database)"
                    :tables-by-db="tablesByDb" :table-comments-by-db="tableCommentsByDb"
                    :can-write="!!conn.canWrite" :commit-handler="onCommitChanges"
                    @run="run(activeBlock)" @execute="sql => executeSql(activeBlock, sql)"
                    @run-shortcut="selected => onRun(activeBlock, selected)"
                    @save="openSave(activeBlock)" @tables="openTables(activeBlock)"
                    @need-columns="tables => onNeedColumns(activeBlock.database, tables)"
                    @need-tables="db => ensureTables(db)" @split-resize="refreshActiveEditor"
                    @favorite="openFavorite" />
            </template>
        </div>

        <db-saved-blocks-drawer ref="savedBlocks" v-model="blocksDrawer" :conn-id="conn.id"
            @load="loadToEditor" @edit="openSave" @favs="openFavList"
            @changed="$emit('changed')" @total="n => savedBlockTotal = n" />

        <db-tables-drawer v-model="tablesDrawer" :conn-id="conn.id" :database="tablesDb" />

        <db-save-block-dialog v-model="saveDialog" :conn-id="conn.id" :block="saveTarget"
            @saved="onBlockSaved" @changed="$emit('changed')" />

        <db-favorite-form-dialog v-model="favoriteDialog" :seed="favoriteSeed" @saved="onFavoriteSaved" />

        <db-favorite-list-dialog ref="favList" v-model="favListDialog" :sql-block-id="favListBlockId"
            :block-name="favListBlockName" @deleted="onFavDeleted" />
    </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { ElMessage } from 'element-plus';
import request from '../../../utils/request.js';
import { executeSql as runSql, commitResultChanges as applyResultChanges } from './dbconsole/sqlExec.js';
import schemaCache from './dbconsole/schemaCache.js';
import DbQueryWorkbench from './dbconsole/DbQueryWorkbench.vue';
import DbSavedBlocksDrawer from './dbconsole/DbSavedBlocksDrawer.vue';
import DbTablesDrawer from './dbconsole/DbTablesDrawer.vue';
import DbSaveBlockDialog from './dbconsole/DbSaveBlockDialog.vue';
import DbFavoriteFormDialog from './dbconsole/DbFavoriteFormDialog.vue';
import DbFavoriteListDialog from './dbconsole/DbFavoriteListDialog.vue';

let blockSeq = 0;

export default {
    name: 'DbConsole',
    components: {
        DbQueryWorkbench,
        DbSavedBlocksDrawer,
        DbTablesDrawer,
        DbSaveBlockDialog,
        DbFavoriteFormDialog,
        DbFavoriteListDialog
    },
    mixins: [schemaCache],
    props: {
        conn: { type: Object, default: () => ({}) }
    },
    emits: ['changed'],
    data() {
        return {
            blocksDrawer: false,
            tablesDrawer: false,
            tablesDb: '',
            databases: [],
            dbLoading: false,
            blocks: [],
            activeBlockUid: '',
            queryNoSeq: 0,
            savedBlockTotal: 0,
            saveDialog: false,
            saveTarget: null,
            favoriteDialog: false,
            favoriteSeed: null,
            favListDialog: false,
            favListBlockId: '',
            favListBlockName: ''
        };
    },
    computed: {
        sqlMode() {
            return this.conn && this.conn.dbType === 'mysql' ? 'text/x-mysql' : 'text/x-pgsql';
        },
        usedDatabases() {
            return Array.from(new Set(this.blocks.map(b => b.database || '')));
        },
        activeBlock() {
            return this.blocks.find(b => b.uid === this.activeBlockUid) || null;
        },
        blocksStorageKey() {
            return this.conn && this.conn.id ? 'hidoc_dbman_blocks_' + this.conn.id : '';
        },
        blocksState() {
            return this.blocks.map(b => ({
                uid: b.uid,
                id: b.id || '',
                name: b.name || '',
                description: b.description || '',
                isShare: !!b.isShare,
                sqlText: b.sqlText || '',
                databaseName: b.databaseName || '',
                database: b.database || '',
                queryNo: b.queryNo || 0
            }));
        }
    },
    watch: {
        usedDatabases: {
            immediate: true,
            handler(list) {
                list.forEach((db) => this.ensureTables(db));
            }
        },
        blocksState() {
            this.debouncedPersistBlocks();
        },
        activeBlockUid() {
            this.debouncedPersistBlocks();
        }
    },
    created() {
        this.debouncedPersistBlocks = debounce(() => {
            this.persistBlocks();
        }, 400);
    },
    beforeUnmount() {
        if (this.debouncedPersistBlocks) {
            this.debouncedPersistBlocks.flush();
        }
    },
    mounted() {
        this.initConsole();
        this.refreshActiveEditor();
        this.$nextTick(() => this.refreshActiveEditor());
    },
    methods: {
        initConsole() {
            this.blocks = [];
            if (!this.restoreBlocks()) {
                this.addBlock();
            }
            this.loadDatabases();
        },
        restoreBlocks() {
            if (!this.blocksStorageKey) return false;
            let saved = null;
            try {
                saved = JSON.parse(localStorage.getItem(this.blocksStorageKey) || 'null');
            } catch (e) {
                saved = null;
            }
            let list = null;
            let active = '';
            if (Array.isArray(saved) && saved.length) {
                list = saved;
            } else if (saved && Array.isArray(saved.blocks) && saved.blocks.length) {
                list = saved.blocks;
                active = saved.active || '';
            }
            if (!list) return false;
            this.blocks = list.map((b, i) => {
                let uid = b && b.uid;
                const num = Number(String(uid || '').replace(/^blk-/, ''));
                if (!uid) {
                    uid = 'blk-' + (++blockSeq);
                } else if (Number.isFinite(num)) {
                    blockSeq = Math.max(blockSeq, num);
                }
                const queryNo = Number(b && b.queryNo) > 0 ? Number(b.queryNo) : (i + 1);
                return {
                    uid,
                    id: (b && b.id) || '',
                    name: (b && b.name) || '',
                    description: (b && b.description) || '',
                    isShare: !!(b && b.isShare),
                    sqlText: (b && b.sqlText) || '',
                    databaseName: (b && b.databaseName) || '',
                    database: (b && b.database) || this.conn.defaultDb || '',
                    queryNo,
                    loading: false,
                    results: [],
                    activeResult: ''
                };
            });
            this.queryNoSeq = this.blocks.reduce((max, b) => Math.max(max, b.queryNo || 0), 0);
            this.activeBlockUid = this.blocks.some(b => b.uid === active)
                ? active
                : (this.blocks[0] && this.blocks[0].uid) || '';
            return true;
        },
        persistBlocks() {
            if (!this.blocksStorageKey) return;
            try {
                localStorage.setItem(this.blocksStorageKey, JSON.stringify({
                    blocks: this.blocksState,
                    active: this.activeBlockUid
                }));
            } catch (e) {
                // 浏览器存储不可用/已满时忽略，不影响主流程
            }
        },
        refreshActiveEditor() {
            const w = this.$refs.workbench;
            if (w && typeof w.refreshEditor === 'function') w.refreshEditor();
        },
        blockTabName(block) {
            const name = block && block.name ? String(block.name).trim() : '';
            return name || ('查询 ' + (block && block.queryNo ? block.queryNo : 1));
        },
        nextQueryNo() {
            this.queryNoSeq += 1;
            return this.queryNoSeq;
        },
        onBlockTabChange() {
            this.$nextTick(() => this.refreshActiveEditor());
        },
        loadDatabases() {
            this.dbLoading = true;
            return request({
                url: '/dbManExec/databases',
                method: 'post',
                data: { connId: this.conn.id }
            }).then(res => {
                if (res.code == 0) {
                    this.databases = res.meta.databases || [];
                }
            }).finally(() => {
                this.dbLoading = false;
            });
        },
        addBlock(seed) {
            const block = {
                uid: 'blk-' + (++blockSeq),
                id: (seed && seed.id) || '',
                name: (seed && seed.name) || '',
                description: (seed && seed.description) || '',
                isShare: (seed && seed.isShare) || false,
                sqlText: (seed && seed.sqlText) || '',
                databaseName: (seed && seed.databaseName) || '',
                database: (seed && (seed.database || seed.databaseName)) || this.conn.defaultDb || '',
                loading: false,
                results: [],
                activeResult: '',
                queryNo: this.nextQueryNo()
            };
            this.blocks.push(block);
            this.activeBlockUid = block.uid;
            this.$nextTick(() => this.refreshActiveEditor());
            return block;
        },
        removeBlockByUid(uid) {
            const idx = this.blocks.findIndex(b => b.uid === uid);
            if (idx < 0) return;
            this.blocks.splice(idx, 1);
            if (!this.blocks.length) this.queryNoSeq = 0;
            if (this.activeBlockUid === uid) {
                const next = this.blocks[idx] || this.blocks[idx - 1] || null;
                this.activeBlockUid = next ? next.uid : '';
            }
        },
        async run(block) {
            await runSql(this.conn, block, block.sqlText);
        },
        async onRun(block, selected) {
            const text = (selected || '').trim();
            await runSql(this.conn, block, text || block.sqlText);
        },
        async executeSql(block, sqlText) {
            await runSql(this.conn, block, sqlText);
        },
        onCommitChanges(block, index, payload) {
            return applyResultChanges(this.conn, block, index, payload, this.$store.state.user.token);
        },
        openSave(block) {
            this.saveTarget = block;
            this.saveDialog = true;
        },
        onBlockSaved(payload) {
            const block = payload && payload.block;
            const saved = payload && payload.saved;
            if (block && saved) {
                block.id = saved.id;
                block.name = saved.name;
                block.description = saved.description;
                block.isShare = saved.isShare;
                block.databaseName = saved.databaseName || block.database || block.databaseName;
            }
            const drawer = this.$refs.savedBlocks;
            if (drawer && typeof drawer.load === 'function') drawer.load();
        },
        loadToEditor(sb) {
            const seed = sb.ownedByMe
                ? { id: sb.id, name: sb.name, description: sb.description, isShare: sb.isShare, sqlText: sb.sqlText, databaseName: sb.databaseName }
                : { name: sb.name + '（副本）', description: sb.description, sqlText: sb.sqlText, databaseName: sb.databaseName };
            this.addBlock(seed);
            ElMessage({ message: '已载入到编辑区', type: 'success', duration: 1500 });
        },
        openTables(block) {
            this.tablesDb = (block && block.database) || this.conn.defaultDb || '';
            this.tablesDrawer = true;
        },
        openFavorite(block, rows, result, sql) {
            if (!block.id) {
                ElMessage({ message: '请先保存该 SQL 块，再进行结果行收藏', type: 'warning', duration: 3000 });
                return;
            }
            if (!rows || rows.length === 0) {
                ElMessage({ message: '请先勾选要收藏的行', type: 'warning', duration: 2000 });
                return;
            }
            if (rows.length > 100) {
                ElMessage({ message: '一次最多收藏 100 行', type: 'warning', duration: 2000 });
                return;
            }
            this.favoriteSeed = {
                sqlBlockId: block.id,
                blockName: block.name,
                name: (block.name || '结果') + '-结果',
                description: '',
                isShare: false,
                sql: sql || block.sqlText || '',
                columns: (result && result.columns) || [],
                rows
            };
            this.favoriteDialog = true;
        },
        onFavoriteSaved(payload) {
            const drawer = this.$refs.savedBlocks;
            if (drawer && payload && payload.sqlBlockId) {
                drawer.adjustFavoriteCount(payload.sqlBlockId, 1);
            }
            if (this.favListDialog && payload && payload.sqlBlockId === this.favListBlockId) {
                const list = this.$refs.favList;
                if (list && typeof list.load === 'function') list.load();
            }
        },
        openFavList(sb) {
            this.favListBlockId = sb.id;
            this.favListBlockName = sb.name;
            this.favListDialog = true;
        },
        onFavDeleted() {
            const drawer = this.$refs.savedBlocks;
            if (drawer && this.favListBlockId) {
                drawer.adjustFavoriteCount(this.favListBlockId, -1);
            }
        }
    }
};
</script>

<style>
.dbconsole-drawer.dbconsole-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    background-color: #ffffff;
}

.dbconsole-panel__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    flex: 0 0 auto;
    min-width: 0;
    padding: 8px 14px;
    background-color: #f3f6fb;
}

.dbconsole-panel__add {
    margin-left: auto;
}

.dbconsole-panel__blocks-count {
    margin-left: 4px;
    padding: 0 6px;
    border-radius: 999px;
    background-color: #eef3fc;
    color: #2f6fed;
    font-size: 12px;
}

.dbconsole-head__name {
    font-size: 15px;
    font-weight: 600;
    color: #1f2d3d;
}

.dbconsole-head__addr {
    font-size: 13px;
    color: #8a94a6;
}

.dbconsole-body {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
}

.dbconsole-drawer .dbconsole-empty {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #aab4c4;
    font-size: 13px;
}

/* 二层：查询文档条。下划线选中，比连接层更轻、更贴近编辑区 */
.dbconsole-tabs {
    flex: 0 0 auto;
    background-color: #f3f6fb;
}

.dbconsole-tabs .el-tabs__header {
    margin: 0;
    padding: 0 10px;
    border: none;
    background-color: #f3f6fb;
}

.dbconsole-tabs .el-tabs__nav-wrap::after {
    height: 1px;
    background-color: #eef1f6;
}

.dbconsole-tabs .el-tabs__active-bar {
    height: 2px;
    border-radius: 2px 2px 0 0;
    background-color: #2f6fed;
}

.dbconsole-tabs .el-tabs__content {
    display: none;
}

.dbconsole-tabs .el-tabs__item {
    height: 32px;
    padding: 0 12px;
    border: none;
    color: #8a94a6;
    font-size: 12px;
    font-weight: 500;
    line-height: 32px;
    transition: color 0.15s;
}

.dbconsole-tabs .el-tabs__item:hover {
    color: #1f2d3d;
}

.dbconsole-tabs .el-tabs__item.is-active {
    color: #1f2d3d;
    font-weight: 600;
}

.dbconsole-tabs .el-tabs__item .is-icon-close {
    width: 14px;
    margin-left: 4px;
    color: #b3bcc9;
}

.dbconsole-tabs .el-tabs__item.is-active .is-icon-close,
.dbconsole-tabs .el-tabs__item .is-icon-close:hover {
    color: #5b6a80;
}

.dbconsole-query-tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 180px;
    vertical-align: middle;
}

.dbconsole-query-tab__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
